"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Cookie, Shield, Settings, BarChart, FileText, Users } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
const cookieCategories = [{
  id: "essential",
  name: "Essential Cookies",
  icon: Shield,
  description: "These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt-out of these cookies as they are required for the basic operation of our services."
}, {
  id: "functional",
  name: "Functional Cookies",
  icon: Settings,
  description: "Functional cookies help us provide enhanced features and personalization. They may be set by us or by third-party providers whose services we use. These cookies remember your preferences and choices to improve your experience on our website."
}, {
  id: "analytics",
  name: "Analytics Cookies",
  icon: BarChart,
  description: "Analytics cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This data helps us improve our services and understand which pages are most and least popular, enabling us to optimize your experience."
}, {
  id: "performance",
  name: "Performance Cookies",
  icon: FileText,
  description: "Performance cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us know which pages are the most and least popular and see how visitors move around the site, ensuring optimal loading times and functionality."
}, {
  id: "targeting",
  name: "Targeting Cookies",
  icon: Users,
  description: "Targeting cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant ads on other sites. These cookies work by uniquely identifying your browser and device."
}, {
  id: "preferences",
  name: "Preference Cookies",
  icon: Cookie,
  description: "Preference cookies enable our website to remember information that changes the way the website behaves or looks, such as your preferred language or the region you are in. These cookies enhance your user experience by providing personalized content and features."
}];
export default function CookieTypesAndPurposes() {
  const [activeCategory, setActiveCategory] = useState("essential");
  const {
    ref: containerRef,
    isVisible: containerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="cookie-types-and-purposes" ref={containerRef} className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className={`mb-12 text-center transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${containerVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
          <h2 className="text-foreground mb-4">Cookie Types and Purposes</h2>
          <p className="text-muted-foreground mx-auto max-w-2xl">
            Learn about the different types of cookies we use and how they help improve your experience on our website.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {}
          <div className="lg:col-span-4">
            <nav className="space-y-1">
              {cookieCategories.map((category, index) => {
              const Icon = category.icon;
              const {
                ref,
                isVisible
              } = useRevealOnScroll({
                threshold: 0.3
              });
              return <button key={category.id} data-index={index} ref={ref} onClick={() => setActiveCategory(category.id)} className={`flex w-full items-center gap-4 rounded-[var(--radius-md)] border border-border px-4 py-3 text-left transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${activeCategory === category.id ? "bg-primary-light border-primary text-primary shadow-soft" : "bg-background hover:bg-surface hover:border-border-strong"} ${isVisible ? "translate-x-0 opacity-100" : "-translate-x-5 opacity-0"}`} style={{
                transitionDelay: `${index * 75}ms`
              }}>
                    <Icon className={`size-5 shrink-0 transition-colors duration-[var(--duration-fast)] ${activeCategory === category.id ? "text-primary" : "text-muted-foreground"}`} />
                    <span className={`font-semibold text-sm ${activeCategory === category.id ? "text-primary-dark" : "text-foreground"}`}>
                      {category.name}
                    </span>
                  </button>;
            })}
            </nav>
          </div>

          {}
          <div className="hidden lg:block lg:col-span-1">
            <Separator orientation="vertical" className="h-full w-px bg-border" />
          </div>

          {}
          <div className="lg:col-span-7">
            {cookieCategories.map(category => {
            const Icon = category.icon;
            const {
              ref,
              isVisible
            } = useRevealOnScroll({
              threshold: 0.2
            });
            return <div key={category.id} ref={ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${activeCategory === category.id ? "block" : "hidden"} ${isVisible && activeCategory === category.id ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="rounded-[var(--radius-md)] bg-primary-light p-3">
                      <Icon className="size-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-foreground mb-2">{category.name}</h3>
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    {category.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}